// saveDefectEdit — saving and deleting an item that already exists, shared by
// the plan's pin card and the register's ⋮ menu.
//
// 2026-10-29 (v0.263.0) — lifted out of PlanScreen's onSheetSave /
// onSheetDelete when the register gained the same card (Brett: "the same one
// when we edit the pin in plan view"). This owns the network and the outbox;
// each screen keeps its own state updates.
import { apiSend } from '../api';
import { addToOutbox, listOutbox, updateOutboxItem, removeFromOutbox, discardOutboxItems } from './outbox.js';
import { ref as outboxRef } from './outbox-refs.js';
import { isLocalId, rowIdFromLocalId, pendingPhotoDeleteIds, mergeQueuedPatch, dependentRowIds } from './outbox-local.js';
import { getCurrentIdentityKey } from './outbox-identity.js';
import { makePhotoBlobs } from './imageVariants';

// The card's form arrives in the UI shape. `assignee` goes out as `assignedTo`;
// the photo lists are split off — photos are saved by their own endpoint after
// the defect.
export function splitDefectForm(form) {
  const { photos, removedPhotoIds, assignee, ...rest } = form || {};
  return {
    apiPayload: { ...rest, assignedTo: assignee || null },
    newPhotos: Array.isArray(photos) ? photos.filter(p => p && !p.id && p.file) : [],
    removedPhotoIds: Array.isArray(removedPhotoIds) ? removedPhotoIds : [],
  };
}

// v0.171.0 — a photo goes into the outbox as BYTES (see imageVariants.js and
// outbox.js), never as a base64 string. `p.prepared` was started the moment the
// photo was picked, so the downscale is normally finished by the time Save is
// tapped and this awaits nothing.
export async function queueDefectPhoto(defectKey, p) {
  const v = p.prepared ? await p.prepared : await makePhotoBlobs(p.file);
  await addToOutbox('POST', '/api/defect-photos', {
    defectId: defectKey,
    caption: p.name || null,
    width: v.width || null,
    height: v.height || null,
    // v0.328.0 - a ghost-camera shot names the photo it answers; the server
    // checks it is this item's own photo before it labels the pair.
    ...(p.kind === 'after' && p.beforePhotoId != null ? { kind: 'after', beforePhotoId: p.beforePhotoId } : {}),
  }, { blobs: { url: v.original, thumbnailUrl: v.thumbnail, displayUrl: v.display } });
}

// v0.69.0 — reconcile REMOVED photos: delete any server-side photo the user
//   took off the strip. Queued on the durable outbox (same path as the add
//   above) so it survives poor reception. Without this the delete never
//   reached the server, so the photo re-hydrated on reopen.
// v0.171.0 — a photo that never left the phone is simply dropped from the
//   queue; there is nothing on the server to delete.
// 2026-09-15 — and never a second delete for a photo already waiting on one
//   (the second came back 404 and sat in the sync strip as refused).
export async function dropDefectPhoto(pid) {
  if (pid == null) return;
  if (isLocalId(pid)) { await removeFromOutbox(rowIdFromLocalId(pid)); return; }
  const queuedDeletes = pendingPhotoDeleteIds(await listOutbox(), getCurrentIdentityKey());
  if (queuedDeletes.has(String(Number(pid)))) return;
  await addToOutbox('DELETE', `/api/defect-photos/${Number(pid)}`, null);
}

// Save the card's form against an existing item. Returns what the screen needs
// to show the edit: whether it only exists in the queue (`local`), the payload
// sent, and the server's answer (`updated` — a { queued } marker when held).
export async function saveDefectEdit(defect, form) {
  const { apiPayload, newPhotos, removedPhotoIds } = splitDefectForm(form);
  let updated = null;
  const local = isLocalId(defect.id);
  if (local) {
    // v0.171.0 — the pin only exists in the queue. Fold the edit INTO its
    // queued create; never queue a PATCH against an id the server has not
    // seen. Photos point at the queued create by reference, as before.
    const rowId = rowIdFromLocalId(defect.id);
    const ok = await updateOutboxItem(rowId, (row) => ({ ...row, body: mergeQueuedPatch(row.body, apiPayload) }));
    if (!ok) throw new Error('This pin is no longer waiting to sync — reload and try again.');
    for (const p of newPhotos) await queueDefectPhoto(outboxRef(rowId), p);
  } else {
    updated = await apiSend('PATCH', `/api/defects/${defect.id}`, apiPayload);
    for (const p of newPhotos) await queueDefectPhoto(Number(defect.id), p);
  }
  for (const pid of removedPhotoIds) await dropDefectPhoto(pid);
  return { local, apiPayload, updated };
}

// Delete an item. One that never left the phone is dropped from the queue with
// every photo / comment queued behind it — nothing to tell the server. Anything
// else is DELETE /api/defects/:id, which is a hard delete.
export async function deleteDefect(defect) {
  if (isLocalId(defect.id)) {
    const rowId = rowIdFromLocalId(defect.id);
    const rows = await listOutbox();
    await discardOutboxItems([rowId, ...dependentRowIds(rows, rowId)]);
    return { local: true };
  }
  await apiSend('DELETE', `/api/defects/${defect.id}`);
  return { local: false };
}
